import Home from './pages/home/Home';
import Site from './pages/site/Site';
import Overlay from './overlay/Overlay';
import anime from 'animejs/lib/anime.es.js';
import { useState, useEffect } from 'preact/hooks';
import LoadingScreen from './loadingScreen/loadingScreen';
import style from '../style/style';

const App = () => {
	let img = new Image();
	const LATEST_EPISODE_NUMBER = 7;
	
	const [loadedImages, setLoadedImages] = useState([]);
	const [site, setSite] = useState(0);
	const [overlayVisible, setOverlayVisible] = useState(false);
	const [overlayContent, setOverlayContent] = useState('');
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		const siteFromUrl = window.location.hash.split('episode=')[1];

		if (siteFromUrl > LATEST_EPISODE_NUMBER) {
			setSite(0);
			window.location.hash = ''
		}
		
		if (!isNaN(siteFromUrl)) {
			setSite(Number(siteFromUrl));
		} else {
			setSite(0);
		}
	}, []);

	window.onhashchange = function() {
		const newSite = window.location.hash.split('episode=')[1] || 0;
		setSite(Number(newSite) <= LATEST_EPISODE_NUMBER ? Number(newSite) : 0);
	}

	useEffect(() => {
		// preload computer images for smooth animations - no animations on mobile, therefore not preloading
		if (window.innerWidth < 960 || loadedImages.length === LATEST_EPISODE_NUMBER + 1) {
			setIsLoading(false);
			return;
		}
		let computerImages = [];
		let imagesLoaded = 0;

		for (let i = 0; i <= LATEST_EPISODE_NUMBER ; i++) {
			computerImages[i] = new Image();
		}

		computerImages.forEach((image, index) => {
			image.src = getComputerImgSrc(index);
			image.onload = () => {
				imagesLoaded++;
				if (imagesLoaded === LATEST_EPISODE_NUMBER) {
					setLoadedImages(computerImages);
					setIsLoading(false);
				}
			}
		});

		setTimeout(() => {
			setIsLoading(false);
		}, 7000);
	}, []);

	const getComputerImgSrc = (newSite = site) => {
		if (newSite === 0) {
			return 'https://docs.rferl.org/infographics/2019/2019_07/2019_07_HolyWar/images/computer-0.png'
		}
		if (newSite === 1) {
            return 'https://docs.rferl.org/infographics/2019/2019_07/2019_07_HolyWar/images/computer-1.png'
		}
		if (newSite === 2) {
			return 'https://docs.rferl.org/infographics/2019/2019_07/2019_07_HolyWar/images/computer-2.png'
		}
        if (newSite === 3) {
            return 'https://docs.rferl.org/infographics/2019/2019_07/2019_07_HolyWar/images/computer-3.png'
        }
        if (newSite === 4) {
            return 'https://docs.rferl.org/infographics/2019/2019_07/2019_07_HolyWar/images/computer-4.png'
        }
        if (newSite === 5) {
            return 'https://docs.rferl.org/infographics/2019/2019_07/2019_07_HolyWar/images/computer-5.png'
        }
        if (newSite === 6) {
            return 'https://docs.rferl.org/infographics/2019/2019_07/2019_07_HolyWar/images/computer-6.png'
        }
        if (newSite === 7) {
            return 'https://docs.rferl.org/infographics/2019/2019_07/2019_07_HolyWar/images/computer-7.png'
        }
	};

	// to calculate how much app needs to be squeezed to fit in the computer frame in animation
	const getAspectRatioFix = (computerImgAspectRatio) => {
		const browserAspectRatio = window.innerWidth / window.innerHeight;
		return browserAspectRatio / computerImgAspectRatio;
	};

	const addSlideOutAnimation = (el, options, newSite) => {
		const { scaleFrom, scaleTo, isComputerFrame, isReturn, squeezeFactor } = options;
		
		if (isComputerFrame) {
			el.style.transform = `scale(${scaleFrom})`; 		
		}

		const vid = document.getElementsByTagName('video')[0];
		if (vid) {
			vid.setAttribute('style', 'width: 100%');
		}

		anime({
			targets: el,
			keyframes: [
				{ 
					scaleY: squeezeFactor, duration: 100
				},
				{ 
					scale: scaleTo,
					easing: 'easeOutCirc',
				},
				{ 
					translateX: `${isReturn ? '' : '-'}${100/options.scaleTo}%`,
					easing: 'easeInCirc',
					duration: 1000,
				},
			],
			duration: 5500,
			complete: () => {
				changeSite(el, options, newSite)
				isComputerFrame || setSite(newSite);
			},
		});
	};

	
	// changes the site in state which renders the next episode, then launches the slide-in animation
	const changeSite = (el, incomingOptions, newSite) => {
		img.setAttribute('src', getComputerImgSrc(newSite));
		const options = getAnimOptions({ isComputerFrame: incomingOptions.isComputerFrame, isReturn: incomingOptions.isReturn }, newSite);

		// clear previous style and move element outside of the screen
		el.setAttribute('style', '');
		el.style.transform = `translateX(${options.isReturn ? '-' : ''}100%)`;
		
		if (!incomingOptions.isComputerFrame) {
			el.style.transform = `${el.style.transform} scaleY(${options.squeezeFactor})`; 
		}
		
		addSlideInAnimation(el, options);
	};


	const addSlideInAnimation = (el, options) => {
		const { scaleFrom, scaleTo, isComputerFrame, squeezeFactor } = options;

		anime({
			targets: el,
			keyframes: [
				{
					duration: 500,
				},
				{
					scale: [scaleTo, scaleTo],
					scaleY: !isComputerFrame ? squeezeFactor : 1,
					translateX: 0,
					easing: 'easeOutCirc',
				},
				{ 
					scale: [scaleTo, scaleFrom],
					easing: 'easeInCirc',
				},
				{
					opacity: isComputerFrame ? 0 : 1,
					scaleY: 1,
					easing: 'easeInCirc',
					duration: 200,
				}
			],
			duration: 5500,
			complete: () => {
				document.body.querySelector('#computerFrame') ? document.body.querySelector('#computerFrame').remove() : '';
				document.querySelector('#traverse').setAttribute('style', '');
				document.querySelector('#traverse').setAttribute('class', '');
				el.style = '';
			},
		});
	};

	const getAnimOptions = ({ isComputerFrame, isReturn }, siteToAnimate = site) => {
		const defaultOptions = {
			isComputerFrame,
			isReturn,
		};

		if (siteToAnimate === 0) {
			if (isComputerFrame) {
				return {
					scaleFrom: 1.5,
					scaleTo: .75,
					squeezeFactor: 1,
					...defaultOptions,
				}
			} else {
				return {
					scaleFrom: 1,
					scaleTo: .5,
					squeezeFactor: getAspectRatioFix(1.6),
					...defaultOptions,
				}
			}
		}

		if (siteToAnimate === 1) {
			if (isComputerFrame) {
				return {
					scaleFrom: 2.1,
					scaleTo: .5,
					squeezeFactor: 1,
					...defaultOptions,
				}
			} else {
				return {
					scaleFrom: 1,
					scaleTo: .24,
					squeezeFactor: getAspectRatioFix(1.3),
					...defaultOptions,
				}
			}
		}
		
		if (siteToAnimate === 2) {
			if (isComputerFrame) {
				return {
					scaleFrom: 2.5,
					scaleTo: .8,
					squeezeFactor: 1,
					...defaultOptions,
				}
			} else {
				return {
					scaleFrom: 1,
					scaleTo: .32,
					squeezeFactor: getAspectRatioFix(1.6),
					...defaultOptions,
				}
			}
		}

        if (siteToAnimate === 3) {
            if (isComputerFrame) {
                return {
                    scaleFrom: 2.3,
                    scaleTo: .7,
                    squeezeFactor: 1,
                    ...defaultOptions,
                }
            } else {
                return {
                    scaleFrom: 1,
                    scaleTo: .30,
                    squeezeFactor: getAspectRatioFix(1.5),
                    ...defaultOptions,
                }
            }
        }
        if (siteToAnimate === 4) {
            if (isComputerFrame) {
                return {
                    scaleFrom: 1.5,
                    scaleTo: .75,
                    squeezeFactor: 1,
                    ...defaultOptions,
                }
            } else {
                return {
                    scaleFrom: 1,
                    scaleTo: .5,
                    squeezeFactor: getAspectRatioFix(1.6),
                    ...defaultOptions,
                }
            }
        }
        // site 5
        if (siteToAnimate === 5) {
            if (isComputerFrame) {
                return {
                    scaleFrom: 2.25,
                    scaleTo: .75,
                    squeezeFactor: 1,
                    ...defaultOptions,
                }
            } else {
                return {
                    scaleFrom: 1,
                    scaleTo: .33,
                    squeezeFactor: getAspectRatioFix(1.7),
                    ...defaultOptions,
                }
            }
        }
        // site 6
        if (siteToAnimate === 6) {
            if (isComputerFrame) {
                return {
                    scaleFrom: 1.15,
                    scaleTo: .38,
                    squeezeFactor: 1,
                    ...defaultOptions,
                }
            } else {
                return {
                    scaleFrom: 1,
                    scaleTo: .33,
                    squeezeFactor: getAspectRatioFix(1.8),
                    ...defaultOptions,
                }
            }
        }
        // site 7
        if (siteToAnimate === 7) {
            if (isComputerFrame) {
                return {
                    scaleFrom: 1.35,
                    scaleTo: .38,
                    squeezeFactor: 1,
                    ...defaultOptions,
                }
            } else {
                return {
                    scaleFrom: 1,
                    scaleTo: .28,
                    squeezeFactor: getAspectRatioFix(1.7),
                    ...defaultOptions,
                }
            }
        }
	};

	
	// orchestrates the flow of animations and site change
	const handleSiteChange = (newSite = site + 1, isReturn, loadedImages) => {
		const timeline = document.querySelector('#timeline');
		if (timeline) {
			timeline.scrollTop = 0;
		}

		if (window.innerWidth < 960) {
			setSite(newSite);
			return;
		}
		const appElement = document.querySelector('#app');
		const frameContainer = document.querySelector('#traverse')

		img.src = getComputerImgSrc(site);
		img.setAttribute('class', 'computer-frame');
		img.setAttribute('id', 'computerFrame');
		img.setAttribute('style', 'width: 100%');

        frameContainer.setAttribute('class', 'computer-frame-container');
		frameContainer.appendChild(img);
		
		// slide-out animation calls site change and slide-in once animation is finished
		addSlideOutAnimation(img, getAnimOptions({ isComputerFrame: true, isReturn }), newSite);
		addSlideOutAnimation(appElement, getAnimOptions({ isComputerFrame: false, isReturn }), newSite);
	};

	const setOverlay = (e, content) => {
		e.preventDefault();
		if (content === overlayContent) {
			setOverlayVisible(false);
			setOverlayContent('');
		} else {
			setOverlayVisible(true);
			setOverlayContent(content);
		}
	};
	
	const closeOverlay = e => {
		setOverlayVisible(false);
		setOverlayContent('');	
	};

	return (
		<div id="container">	
			{isLoading && <LoadingScreen/>}
			<Overlay 
				isVisible={overlayVisible}
				setVisible={setOverlayVisible}
				content={overlayContent}
				site={site}
				closeOverlay={closeOverlay}
				handleSiteChange={handleSiteChange}
				loadedImages={loadedImages}
			/>
			<div id="traverse"/>
			<div id="app">
				{site === 0 &&
					<Home 
						handleSiteChange={handleSiteChange}
						site={site}
						overlayVisible={overlayVisible}
						setOverlay={setOverlay}
						loadedImages={loadedImages}
						closeOverlay={closeOverlay}
						/>
					}
				{site >= 1 && 
					<Site 
						handleSiteChange={handleSiteChange}
						site={site}
						overlayVisible={overlayVisible}
						setOverlay={setOverlay}
						loadedImages={loadedImages}
						closeOverlay={closeOverlay}
						/>
					}
			</div>
		</div>
	);
}

export default App;
